import { cn } from '@/lib/utils'
import { useGameRegistry } from '@/hooks/useGameRegistry'
import type { Game } from '@/types/game'

interface GameOption {
  id: string
  name: string
  description: string
}

interface GameTabsProps {
  selectedGameId: string
  onGameChange: (gameId: string) => void
  disabled?: boolean
  className?: string
}

export function GameTabs({ 
  selectedGameId, 
  onGameChange, 
  disabled = false,
  className 
}: GameTabsProps) {
  // Get games from registry
  const { games, loading: gamesLoading } = useGameRegistry()

  // Filter to only published games and create tab structure
  const publishedGames = games.filter((game: Game) => game.isPublished)
  const gameOptions: GameOption[] = [
    { id: 'all', name: 'All Games', description: 'Longest streak across all games' },
    ...publishedGames.map((game: Game) => ({
      id: game.id,
      name: game.displayName,
      description: 'Stars earned today'
    }))
  ]

  // Handle game tab change
  const handleGameChange = (gameId: string) => {
    if (disabled) return
    onGameChange(gameId)
  }

  // Find selected game for description
  const selectedGame = gameOptions.find(game => game.id === selectedGameId) || gameOptions[0]

  // Don't render if still loading games or no games available
  if (gamesLoading || gameOptions.length === 0) {
    return null
  }

  return (
    <div className={className}>
      {/* Game Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-600 -mx-4 sm:mx-0">
        <div className="flex space-x-1 overflow-x-auto px-4 sm:px-0" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {gameOptions.map((game) => (
            <button
              key={game.id}
              onClick={() => handleGameChange(game.id)}
              disabled={disabled}
              className={cn(
                'px-4 sm:px-6 py-3 font-oswald text-sm font-medium uppercase tracking-[0.5px] whitespace-nowrap',
                'border-b-2 transition-colors duration-200',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                selectedGameId === game.id
                  ? 'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300 dark:hover:border-gray-500'
              )}
              aria-label={`Switch to ${game.name} leaderboard`}
            >
              {game.name}
            </button>
          ))}
        </div>
      </div>

      {/* Game Description */}
      {selectedGame && (
        <div className="mt-4 text-center">
          <p className="font-oswald text-sm uppercase tracking-[0.5px] text-muted-foreground">
            {selectedGame.description}
          </p>
        </div>
      )}
    </div>
  )
}

export default GameTabs